// Viewport hook — picks the layout: phone (single-page shell + bottom tabs)
// or desktop (TopNav + page/chat split).
//
// The answer is read from the browser on every render (useSyncExternalStore)
// rather than cached in React state. A cached copy only changes when a
// MediaQueryList `change` event arrives, and an installed Android PWA can miss
// one (e.g. the window is resized while backgrounded), leaving a phone stuck
// in the wrong layout until reload. Reading live, plus re-checking on resize
// and on return to the foreground, makes a missed event correct itself.
import { useSyncExternalStore } from 'react';

/** Phone: narrower than Tailwind's `md` (768px), or a short touch screen —
 *  a phone held sideways is wider than 768px but under 500px tall. The
 *  `pointer: coarse` guard keeps a short desktop window on the desktop layout. */
const PHONE_QUERY = '(max-width: 767px), (max-height: 500px) and (pointer: coarse)';

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function subscribe(onChange: () => void): () => void {
  if (!hasMatchMedia()) return () => {};
  const mql = window.matchMedia(PHONE_QUERY);
  mql.addEventListener('change', onChange);
  window.addEventListener('resize', onChange);
  window.addEventListener('pageshow', onChange);
  document.addEventListener('visibilitychange', onChange);
  return () => {
    mql.removeEventListener('change', onChange);
    window.removeEventListener('resize', onChange);
    window.removeEventListener('pageshow', onChange);
    document.removeEventListener('visibilitychange', onChange);
  };
}

function getSnapshot(): boolean {
  return hasMatchMedia() && window.matchMedia(PHONE_QUERY).matches;
}

function getServerSnapshot(): boolean {
  return false;
}

/** True on phones in either orientation. Drives the single-page mobile shell
 *  with bottom tab bar and chat-first home. */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
